import { Filterable, QuestionChoiceModified, QuestionOptionKey, QuestionOptionType, SurveyConfigurationType, SurveyEditType, SurveyNotificationsType, SurveyQuestionOptionType, SurveyQuestionType, SurveyShareMode, SurveyState } from "@/common/interfaces";
import { defineStore } from "pinia";
import { Ref, ref } from "vue";
import { Configuration } from "../configuration";
import { EditorQuestion } from "./questions";
import { SurveyQuestionEditEvent, SurveyQuestionOptionEvent } from "../messages";
import { Moment } from "moment";

export declare type LiveDoc = {
	doc: {[key: string]: any};
	version: number;
	clientID: string;
};

export declare type QuestionOptionValues = {
	intValue?: number | null, charValue?: string | null, textValue?: string | null, datetimeValue?: string | null
};

export const useSurveyEditStore = defineStore('surveyEdit', () => {
	const consumerChannel: Ref<string | null> = ref(null);

	const name: Ref<string> = ref('');
	const emoji: Ref<string | null> = ref(null);
	const isTemplate: Ref<boolean> = ref(false);
	const doc: Ref<{[key: string]: any} | null> = ref(null);
	const info: Ref<SurveyEditType | null> = ref(null);
	const configuration: Ref<Configuration | null> = ref(null);
	const loaded: Ref<boolean> = ref(false);

	const notifications: Ref<SurveyNotificationsType | null> = ref(null);

	const questions: Ref<{[key: string]: EditorQuestion}> = ref({});

	const liveDoc: Ref<LiveDoc | null> = ref(null);

	const editingSurvey: Ref<boolean> = ref(false);

	function clearStore() {
		name.value = '';
		doc.value = null;
		info.value = null;
		emoji.value = null;
		isTemplate.value = false;
		loaded.value = false;
		configuration.value = null;
		liveDoc.value = null;
		editingSurvey.value = false;
		questions.value = {};
		notifications.value = null;
	}

	function updateSurvey(survey: SurveyEditType) {
		info.value = survey;
		name.value = survey.name;
		emoji.value = survey.emoji;
		isTemplate.value = survey.isTemplate;
	}

	function setStore(
		surveyInfo: {survey: SurveyEditType, questions: Array<SurveyQuestionType>}
	) {
		clearStore();
		info.value = surveyInfo.survey;
		name.value = surveyInfo.survey.name;
		emoji.value = surveyInfo.survey.emoji;
		isTemplate.value = surveyInfo.survey.isTemplate;
		doc.value = surveyInfo.survey.surveyDoc;
		questions.value = {};
		for (const question of surveyInfo.questions) {
			storeEditorQuestionFromRemote(question);
		}
		loaded.value = true;
	}

	function setCurrentDoc(
		doc: {[key: string]: any},
		version: number
	) {
		if (consumerChannel.value === null) {
			console.error("Attempting to set liveDoc when consumer is not ready!");
			throw new Error("Cannot setCurrentDoc without valid consumer");
		}

		liveDoc.value = {doc, version, clientID: consumerChannel.value};
	}

	function setSurveyName(
		newName: string
	) {
		if (info.value === null) {
			return;
		}

		info.value.name = newName;
		name.value = newName;
	}

	function setTemplate(_isTemplate: boolean): void {
		isTemplate.value = _isTemplate;
	}

	function setSurveyEmoji(newEmoji: string | null) {
		if (info.value === null) {
			return;
		}

		info.value.emoji = newEmoji;

		emoji.value = newEmoji;
	}

	function setSurveyDoc(
		newDoc: {[key: string]: any}
	) {
		if (info.value === null) {
			return;
		}

		info.value.surveyDoc = newDoc;
		doc.value = newDoc;
	}

	const setConsumerChannel = (channel: string): void => {
		consumerChannel.value = channel;
	};

	const updateConfiguration = (newConfiguration: SurveyConfigurationType): void => {
		if (configuration.value === null) {
			configuration.value = new Configuration(newConfiguration);
		} else {
			configuration.value.update(newConfiguration);
		}
	}

	function setShareMode(newMode: SurveyShareMode) {
		if (info.value === null) {
			return;
		}

		info.value.shareMode = newMode;
	}

	function setState(newState: SurveyState) {
		if (info.value === null) {
			return;
		}

		info.value.state = newState;
	}

	function storeEditorQuestionFromRemote(question: SurveyQuestionType): void {
		questions.value[question.uuid] = EditorQuestion.fromRemote(question);
	}

	function removeEditorQuestion(questionUuid: string): void {
		if (questions.value[questionUuid] !== undefined) {
			delete questions.value[questionUuid];
		}
	}

	function updateEditorQuestionOptionsFromEvent(updateInfo: SurveyQuestionOptionEvent): void {
		const question = questions.value[updateInfo.question.uuid];

		if (question) {
			question.updateOptions(updateInfo.question);
		}
	}

	function updateEditorQuestionOptionDirect(
		questionUuid: string, key: QuestionOptionKey, optionType: QuestionOptionType, values: QuestionOptionValues
	): void {
		const question = questions.value[questionUuid];

		if (values.charValue === undefined) {
			values.charValue = null;
		}
		if (values.intValue === undefined) {
			values.intValue = null;
		}
		if (values.datetimeValue === undefined) {
			values.datetimeValue = null;
		}
		if (values.textValue === undefined) {
			values.textValue = null;
		}

		if (question) {
			question.options.set(key, {
				key, optionType, ...values
			} as SurveyQuestionOptionType);
		}

		if (info.value) info.value.hasUnpublishedChanges = true;
	}

	function updateEditorQuestionFromEvent(updateInfo: SurveyQuestionEditEvent): void {
		const question = questions.value[updateInfo.questionUuid];

		if (question) {
			switch(updateInfo.field) {
				case 'required':
					question.required = updateInfo.value;
				break;
				case 'identifier':
					question.identifier = updateInfo.value;
				break;
				case 'choiceBadge':
					question.choiceBadge = updateInfo.value;
				break;
				case 'displayStyle':
					question.displayStyle = updateInfo.value;
				break;
				case 'randomizeChoices':
					question.randomizeChoices = updateInfo.value;
				break;
				case 'limitMin':
					question.limitMin = updateInfo.value;
				break;
				case 'limitMax':
					question.limitMax = updateInfo.value;
				break;
			}
		}

		if (info.value) info.value.hasUnpublishedChanges = true;
	}

	function updateEditorQuestionChoices(questionUuid: string, choiceModifications: Array<QuestionChoiceModified>): void {
		if (questions.value[questionUuid] === undefined) {
			return;
		}

		questions.value[questionUuid].updateChoices(choiceModifications);
		if (info.value) info.value.hasUnpublishedChanges = true;
	}

	function getQuestion(questionUuid: string): EditorQuestion | null {
		const question = questions.value[questionUuid];
		return (question)? question : null;
	}

	function setNotifications(notifications_: SurveyNotificationsType): void {
		notifications.value = notifications_;
	}

	return {
		name, emoji, doc, info, loaded, liveDoc, consumerChannel, configuration, notifications,
		isTemplate, setTemplate, updateSurvey, editingSurvey,
		setSurveyName, setSurveyDoc, setCurrentDoc, setSurveyEmoji,
		clearStore, setStore, setConsumerChannel,
		getQuestion, updateEditorQuestionFromEvent,
		storeEditorQuestionFromRemote, removeEditorQuestion, updateEditorQuestionChoices,
		updateEditorQuestionOptionsFromEvent, updateEditorQuestionOptionDirect,
		updateConfiguration,
		setShareMode, setState,
		setNotifications
	};
});
